import { useEffect, useState } from 'react';
import moment from 'moment';
import * as Yup from 'yup';
import classnames from 'classnames';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CONTACT_EMAIL } from '@/helpers/constants';
import { RoundButton } from '@/components/UI/RoundButton';
import { APP_LINKS } from '@/helpers/links';
import { useSendPhoneVerificationCode } from '@/query/queries/user/useSendPhoneVerificationCode';
import { useGetPhoneVerificationStatus } from '@/query/queries/user/useGetPhoneVerificationStatus';
import { CountdownHour } from '../../CountdownHour';
import { MyLink } from '../../MyLink';
import { VerificationCodeInput } from '../../UI/VerificationCodeInput';
import s from './index.module.scss';

const VALIDATION_SCHEMA = Yup.object({
  code: Yup.array(),
});

const SMS_LIMIT = 3;

const PhoneVerificationForm = ({
  onSubmit,
  captchaToken,
  getCaptchaToken,
  phone = '',
  onLater,
  onBack,
  defaultSubmit,
  className,
}) => {
  const location = useLocation();
  const { data: status } = useGetPhoneVerificationStatus();
  const { mutateAsync: sendVerificationCode } = useSendPhoneVerificationCode();
  const { t } = useTranslation('auth');
  const [expireTime, setExpireTime] = useState(null);

  useEffect(() => {
    if (!status || !status?.last_created_at) {
      setExpireTime(null);
      return;
    }

    const currentTime = moment();
    const expireTime = moment(status.last_created_at).add(
      status.count >= SMS_LIMIT ? 10 : 1,
      'minutes',
    );

    currentTime.isBefore(expireTime)
      ? setExpireTime(expireTime.toISOString())
      : setExpireTime(null);
  }, [status]);

  const resendCode = async () => {
    const token = captchaToken || (await getCaptchaToken());

    if (!token) return;

    await sendVerificationCode({ phone_number: phone, recaptcha_token: token });
  };

  const onCounterFinish = () => {
    setExpireTime(null);
  };

  if (!status) return null;

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={VALIDATION_SCHEMA}
      initialValues={{
        code: [],
      }}
    >
      {({ values, errors, touched, handleSubmit, setFieldValue }) => (
        <form className={classnames(s.wrapper, className)} onSubmit={handleSubmit}>
          <h1 className={classnames(s.title, 'f-500')}>
            {t('phone_verif_form.verify_your_phone_number')}
          </h1>
          <div className={classnames(s.desc, 'f-18-24 f-400')}>
            {t('phone_verif_form.to_continue_please_enter_code')}{' '}
            <span className="f-500">
              {t('phone_verif_form.ending_in', { phone: phone.substring(phone.length - 4) })}
            </span>
            .
          </div>

          <div className={classnames(s.desc, 'f-18-24 f-400')}>
            {t('phone_verif_form.did_not_receive_code')}{' '}
            {expireTime ? (
              <CountdownHour
                endTime={expireTime}
                onFinish={onCounterFinish}
                label={time => t('phone_verif_form.resend_code_in', { time })}
                className={s.countdown}
              />
            ) : (
              <button
                type="button"
                onClick={() => {
                  setFieldValue('code', []);
                  resendCode();
                }}
                className={classnames(s.button_resend, 'underline')}
              >
                {t('phone_verif_form.resend')}
              </button>
            )}
          </div>

          {status.count >= SMS_LIMIT && (
            <div className="f-18-24 f-400" style={{ marginTop: '8px' }}>
              {t('phone_verif_form.technical_problems_contact_support')}{' '}
              <MyLink href={APP_LINKS.email} isExternal underline>
                {CONTACT_EMAIL}
              </MyLink>
              .
            </div>
          )}

          <VerificationCodeInput
            values={values.code}
            className={s.input}
            error={errors.code && touched.code}
            onChange={value => setFieldValue('code', value.split(''))}
            onComplete={() => {
              handleSubmit();
            }}
          />

          {errors.code && touched.code && (
            <div className={classnames(s.error_message, 'f-16-24 f-400')}>{errors.code}</div>
          )}

          <div
            className={classnames(
              s.buttons,
              location.pathname === APP_LINKS.dashboard && s.buttons_row,
            )}
          >
            {onBack && (
              <RoundButton
                label={t('phone_verif_form.back')}
                type="button"
                fullWidth
                onClick={onBack}
                className={s.button_back}
              />
            )}

            <RoundButton
              label={defaultSubmit ? t('phone_verif_form.verify') : t('phone_verif_form.continue')}
              type="submit"
              onSubmit={handleSubmit}
              fillBackground
              fullWidth
              withArrow={!defaultSubmit}
              className={s.button_submit}
            />

            {onLater && (
              <button
                type="button"
                onClick={onLater}
                className={classnames(s.button_later, 'f-16-24 f-400', 'underline')}
              >
                {t('phone_verif_form.verify_later')}
              </button>
            )}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default PhoneVerificationForm;
